import * as app from 'durandal/app';
import * as system from 'durandal/system';
import * as ko from 'knockout';
import * as analytics from 'analytics';

import { DisplayStatusName } from '@/legacy/features/user/shared/constants/accountConstants';
import * as companyService from '@/legacy/services/company';
import * as userService from '@/legacy/services/user';
import * as verificationService from '@/legacy/services/verification/verification';
import * as xhrService from '@/legacy/services/xhrService';
import { serializeQueryWithoutKeyEncoding } from '@/legacy/utils/dotnetcore';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as addModels from '@/legacy/viewmodels/addModels';
import * as viewModels from '@/legacy/viewmodels/viewModels';

import { AccountType } from '../../../types/AccountType';

import { FundAccountLimitTierViewModel } from './fundAccounts/models';

const API_PREFIX = 'v1';

export class BankAccountDetailsAddModel {
  public currency: string;

  public country: string;

  public accountNumber: string;
}

export class CanadianBankAccountDetailsAddModel extends BankAccountDetailsAddModel {
  public institution: string;

  public transit: string;
}
export class UsBankAccountDetailsAddModel extends BankAccountDetailsAddModel {
  public routingNumber: string;
}

export enum BANK_ACCOUNT_DEPOSIT_VALIDATION_STATUS {
  UNKNOWN,
  LOCKED = 10,
  PENDING_DEPOSIT = 20,
  READY = 30,
}
export class BankAccountDepositValidation {
  public constructor(data?: any) {
    this.status = BANK_ACCOUNT_DEPOSIT_VALIDATION_STATUS.UNKNOWN;
    if (data != null) {
      this.status = data.status;
    }
    this.statusValue =
      BANK_ACCOUNT_DEPOSIT_VALIDATION_STATUS[BANK_ACCOUNT_DEPOSIT_VALIDATION_STATUS[this.status]];
  }

  public status: number;

  public statusValue: BANK_ACCOUNT_DEPOSIT_VALIDATION_STATUS;
}

export class AccountUniversalPayee {
  public isReady: KnockoutComputed<boolean> = ko.pureComputed({
    read: () => this.statusValue() === 'READY',
    deferEvaluation: true,
  });

  public isPending: KnockoutComputed<boolean> = ko.pureComputed({
    read: () => this.statusValue() === 'PENDING',
    deferEvaluation: true,
  });

  public id: KnockoutObservable<string> = ko.observable('');

  public status: KnockoutObservable<string> = ko.observable('');

  public statusValue: KnockoutObservable<string> = ko.observable('');

  public Update(accountInfo) {
    for (const property of Object.keys(accountInfo)) {
      // if updated data doesn't specify property skip
      if (accountInfo[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(accountInfo[property]);

        if (!ko.isComputed(this[property]) && ko.isObservable(this[property])) {
          this[property](newValue);
        }
      }
    }
  }

  constructor(info: any) {
    this.Update(info);
  }
}

export interface Account {
  id: KnockoutObservable<string>;
  displayName: KnockoutObservable<string>;
  displayType: KnockoutObservable<string>;

  identityValidated: KnockoutObservable<boolean>;

  deleted: KnockoutObservable<boolean>;
  locked: KnockoutObservable<boolean>;
  isReady: KnockoutComputed<boolean>;
  country: KnockoutObservable<string>;
  Update(accountInfo);
  status: KnockoutComputed<string>;
  currency: KnockoutObservable<string>;
  companyId: KnockoutObservable<string>;
  displayStatus: KnockoutObservable<DisplayStatusName>;
  universalPayee: KnockoutObservable<AccountUniversalPayee>;
  additionalAccountApprovers: KnockoutObservableArray<string>;
  type: KnockoutObservable<string>;

  microDeposit: KnockoutObservable<viewModels.KOMicroDepositViewModel>;
  accountDetails: KnockoutObservable<viewModels.KOBankAccountDetailsViewModel>;
  fundAccountLimitTiers: KnockoutObservableArray<FundAccountLimitTierViewModel>;
  isDefaultPayablesAccount: KnockoutObservable<boolean>;
  isDefaultReceivablesAccount: KnockoutObservable<boolean>;
}

export class BankAccount implements Account {
  public id: KnockoutObservable<string> = ko.observable('');

  public displayName: KnockoutObservable<string> = ko.observable('');

  public displayAccountName = ko.observable<string>('');

  public displayType: KnockoutObservable<string> = ko.observable('');

  public bank: KnockoutObservable<string> = ko.observable('');

  public companyId: KnockoutObservable<string> = ko.observable('');

  public fee: KnockoutObservable<number> = ko.observable(0.0);

  public locked: KnockoutObservable<boolean> = ko.observable(false);

  public currency: KnockoutObservable<string> = ko.observable('');

  public identityValidated: KnockoutObservable<boolean> = ko.observable(false);

  public type: KnockoutObservable<AccountType> = ko.observable(AccountType.Unknown);

  public universalPayee: KnockoutObservable<AccountUniversalPayee> = ko.observable(undefined);

  public deleted: KnockoutObservable<boolean> = ko.observable(false);

  public name: KnockoutObservable<string> = ko.observable('');

  public additionalAccountApprovers = ko.observableArray<string>([]);

  public numberOfApprovers: KnockoutObservable<number> = ko.observable(0);

  public isBillingAccount: KnockoutObservable<boolean> = ko.observable(false);

  // @todo these are PreundedWallet fields, need to move them:
  public country: KnockoutObservable<string> = ko.observable('');

  public verificationStatus = ko.observable<string>(null);

  public availableFund: KnockoutObservable<number> = ko.observable(0.0);

  public microDeposit: KnockoutObservable<viewModels.KOMicroDepositViewModel> = ko.observable(
    new viewModels.KOMicroDepositViewModel(null)
  );

  public accountDetails = ko.observable<viewModels.KOBankAccountDetailsViewModel>(null);

  public fundAccountLimitTiers = ko.observableArray<FundAccountLimitTierViewModel>([]);

  public isDefaultPayablesAccount = ko.observable<boolean>(false);

  public isDefaultReceivablesAccount = ko.observable<boolean>(false);

  // @todo these are CreditCardAccount fields, need to move them:
  public expiryMonth = ko.observable<number>();

  public expiryYear = ko.observable<number>();

  public creditCardBrandType = ko.observable<string>();

  public accountHolderName = ko.observable<string>();

  public registeredBusinessName = ko.observable<string>();

  constructor(info: any) {
    this.Update(info);
  }

  public isEditable: KnockoutComputed<boolean> = ko.computed({
    read: () => true,
    deferEvaluation: true,
  });

  public isDeletable: KnockoutComputed<boolean> = ko.computed({
    read: () => this.type() === AccountType.BankAccount,
    deferEvaluation: true,
  });

  public DisplayCountry: KnockoutComputed<string> = ko.computed({
    read: (): string => {
      switch (this.country()) {
        case 'CA':
          return 'Canada';
        case 'US':
          return 'United States';
        default:
          return this.country();
      }
    },
    deferEvaluation: true,
  });

  public status: KnockoutComputed<string> = ko.computed({
    read: (): string => {
      switch (this.type()) {
        case AccountType.BankAccount:
          if (this.locked()) {
            return 'locked';
          }

          if (
            this.microDeposit() &&
            this.microDeposit().status() === 'CREDIT_FAILED' &&
            this.microDeposit().error()
          ) {
            return 'locked';
          }
          if (!this.bank()) {
            return 'attention';
          }
          if (!this.microDeposit().sent()) {
            return 'depositPending';
          }
          if (!this.isReady()) {
            return 'attention';
          }

          if (this.verificationStatus()) {
            switch (this.verificationStatus()) {
              case 'REVIEWED':
              case 'COMPLETED':
                return 'verified';
              default:
              // fall-through
            }
          }

          return 'attention';
        case AccountType.PrefundedWallet:
          return 'verified';
        case AccountType.CreditCardAccount:
          if (this.locked()) {
            return 'locked';
          }
          switch (this.verificationStatus()) {
            case 'PENDING':
            case 'PROCESSING':
              return 'pending';
            case 'COMPLETED':
              return 'verified';
            default:
              return 'attention';
          }
        default:
          return undefined;
      }
    },
    deferEvaluation: true,
  });

  public displayStatus: KnockoutComputed<DisplayStatusName> = ko.computed({
    read: (): DisplayStatusName => {
      switch (this.type()) {
        case AccountType.BankAccount:
          if (this.locked()) {
            return DisplayStatusName.Locked;
          }
          if (
            this.microDeposit() &&
            this.microDeposit().status() === 'CREDIT_FAILED' &&
            this.microDeposit().error()
          ) {
            return DisplayStatusName.PINDepositFailed;
          }
          if (!this.bank()) {
            return DisplayStatusName.AttentionRequired;
          }
          if (!this.microDeposit().sent()) {
            return DisplayStatusName.PINDepositPending;
          }
          if (!this.isReady()) {
            return DisplayStatusName.AttentionRequired;
          }

          if (this.verificationStatus()) {
            switch (this.verificationStatus()) {
              case 'REVIEWED':
              case 'COMPLETED':
                return DisplayStatusName.Verified;
              default:
              // fall-through
            }
          }

          return DisplayStatusName.VerificationRequired;
        case AccountType.PrefundedWallet:
          return DisplayStatusName.Ready;
        case AccountType.CreditCardAccount:
          if (this.locked()) {
            return DisplayStatusName.Locked;
          }
          switch (this.verificationStatus()) {
            case 'PENDING':
            case 'PROCESSING':
              return DisplayStatusName.Pending;
            case 'COMPLETED':
              return DisplayStatusName.Verified;
            default:
              return DisplayStatusName.VerificationRequired;
          }
        default:
          return undefined;
      }
    },
    deferEvaluation: true,
  });

  public isReady: KnockoutComputed<boolean> = ko.computed({
    read: () => {
      if (this.deleted === undefined || this.deleted() === true) {
        return false;
      }

      // prefunded wallets are automatically ready for payments
      if (this.type() === AccountType.PrefundedWallet) {
        return true;
      }

      if (this.type() === AccountType.CreditCardAccount) {
        return this.verificationStatus() === 'COMPLETED';
      }

      if (this.locked === undefined || this.locked() !== false) {
        return false;
      }
      if (this.bank === undefined || !this.bank()) {
        return false;
      }
      if (this.microDeposit().sent === undefined || this.microDeposit().sent() !== true) {
        return false;
      }
      if (this.microDeposit().validated === undefined || this.microDeposit().validated() !== true) {
        return false;
      }
      return true;
    },
    deferEvaluation: true,
  });

  public Update(accountInfo) {
    for (const property of Object.keys(accountInfo)) {
      // if updated data doesn't specify property skip
      if (accountInfo[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(accountInfo[property]);

        if (property === 'microDeposit') {
          this.microDeposit(new viewModels.KOMicroDepositViewModel(newValue));
        } else if (property === 'accountDetails') {
          this.accountDetails(new viewModels.KOBankAccountDetailsViewModel(newValue));
        } else if (property === 'universalPayee') {
          if (!this.universalPayee()) {
            if (newValue) {
              this.universalPayee(new AccountUniversalPayee(newValue));
            }
          } else {
            this.universalPayee().Update(newValue);
          }
        } else if (!ko.isComputed(this[property]) && ko.isObservable(this[property])) {
          this[property](newValue);
        }
      }
    }
  }
}
export class BankAccountApprover {
  public id: KnockoutObservable<string> = ko.observable('');

  public companyId: KnockoutObservable<string> = ko.observable('');

  public bankAccountId: KnockoutObservable<string> = ko.observable('');

  public userId: KnockoutObservable<string> = ko.observable('');
}
export class BankService {
  private membershipSubscription: KnockoutSubscription = undefined;

  private companyService: companyService.CompanyService;

  private userService: userService.UserService;

  private verificationService: verificationService.VerificationService;

  constructor() {
    this.companyService = companyService.Instance;
    this.userService = userService.Instance;
    this.verificationService = verificationService.Instance;
  }

  public getAccountJsonById(accountId: string): JQueryPromise<any> {
    const companyId: string = this.companyService.CompanyId();
    return system
      .defer<any>((dfd) => {
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/accounts/${accountId}`
          )
          .then((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            dfd.resolve(accountInfo);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public getAccountById(accountId: string): JQueryPromise<BankAccount> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<BankAccount>((dfd) => {
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/accounts/${accountId}`
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            const account = new BankAccount(accountInfo);
            dfd.resolve(account);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public RenameAccount(accountId: string, name: string): JQueryPromise<any> {
    return this.UpdateAccount(accountId, {
      name,
    }).then((updatedAccount) => {
      app.trigger('account:renamed', updatedAccount); // When we map accounts in accounting software it is important for us to update the new payments right away
      return updatedAccount;
    });
  }

  public UpdateAccount(accountId: string, data: unknown): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/accounts/${accountId}`,
            data
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            const account = new BankAccount(accountInfo);
            dfd.resolve(account);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public RemoveAccount(accountId: string): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        this.userService
          .AuthorizedDelete(
            `${
              import.meta.env.ORCHESTRATION_API_BASE_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/accounts/${accountId}`
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            dfd.resolve();
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public ValidateAccountIdentity(accountId: string): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/accounts/${accountId}/validateIdentity`
          )
          .done((accountInfo) => {
            const account = new BankAccount(accountInfo);
            dfd.resolve(account);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public GetAccountDepositValidation(
    accountId: string
  ): JQueryPromise<BankAccountDepositValidation> {
    return system
      .defer<BankAccountDepositValidation>((dfd) => {
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/accounts/${accountId}/validateDeposits`
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              // we lock account on our end if it was locked on the server
              dfd.reject(accountInfo);
              return;
            }
            const validation = new BankAccountDepositValidation(accountInfo);
            dfd.resolve(validation);
          })
          .fail((errorInfo) => {
            dfd.reject(errorInfo);
          });
      })
      .promise();
  }

  public ValidateAccountDeposits(accountId: string, data: unknown): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/accounts/${accountId}/validateDeposits`,
            data
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            const account = new BankAccount(accountInfo);
            dfd.resolve(account);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public CreateAccount(newAccount: addModels.BankAccountAddModel): JQueryPromise<BankAccount> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<BankAccount>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPut(
            `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/accounts`,
            newAccount
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            analytics.send(
              'action',
              // Historical typo.
              // eslint-disable-next-line @cspell/spellchecker
              'acount',
              'account-created',
              companyService.Instance.CompanyId()
            );

            const account = new BankAccount(accountInfo);

            dfd.resolve(account);
          })
          .fail((err) => {
            dfd.reject(err);
          });
      })
      .promise();
  }

  public GetAccountsWithFiltersXHR(
    itemsWithFiltersBindingModel: viewModels.ItemsWithFiltersBindingModel
  ) {
    const companyId: string = this.companyService.CompanyId();

    const dataPromiseWithXhr = new xhrService.PromiseWithXhr<Array<Account>>();
    dataPromiseWithXhr.getXhr = () =>
      this.userService.AuthorizedGetFromUri(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/accounts`,
        serializeQueryWithoutKeyEncoding(itemsWithFiltersBindingModel)
      ) as JQueryXHR;

    dataPromiseWithXhr.dataProcessCallback = (data: Array<viewModels.FundAccountViewModel>) => {
      const koAccounts: Array<Account> = [];
      $.each(data, (index, accountInfo) => {
        koAccounts.push(new BankAccount(accountInfo));
      });

      return koAccounts;
    };

    xhrService.Instance.initiatePromiseWithXhr(dataPromiseWithXhr); // handles both .errors and logout errors

    return dataPromiseWithXhr;
  }

  public GetAccounts(resultsPerPage = 50) {
    const companyId: string = this.companyService.CompanyId();

    const dataPromiseWithXhr = new xhrService.PromiseWithXhr<Array<BankAccount>>();
    dataPromiseWithXhr.getXhr = () =>
      this.userService.AuthorizedGetFromUri(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/accounts`,
        { resultsPerPage }
      ) as JQueryXHR;

    dataPromiseWithXhr.dataProcessCallback = (data: Array<viewModels.FundAccountViewModel>) => {
      const Accounts: Array<BankAccount> = [];
      $.each(data, (index, accountInfo) => {
        Accounts.push(new BankAccount(accountInfo));
      });

      return Accounts;
    };

    xhrService.Instance.initiatePromiseWithXhr(dataPromiseWithXhr); // handles both .errors and logout errors

    return dataPromiseWithXhr;
  }

  public GetFundAccountViewModelsWithFiltersXHR(
    itemsWithFiltersBindingModel: viewModels.ItemsWithFiltersBindingModel
  ) {
    const companyId: string = this.companyService.CompanyId();

    const dataPromiseWithXhr = new xhrService.PromiseWithXhr<
      Array<viewModels.FundAccountViewModel>
    >();
    dataPromiseWithXhr.getXhr = () =>
      this.userService.AuthorizedGetFromUri(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/accounts`,
        serializeQueryWithoutKeyEncoding(itemsWithFiltersBindingModel)
      ) as JQueryXHR;

    dataPromiseWithXhr.dataProcessCallback = (data: Array<viewModels.FundAccountViewModel>) => data;

    xhrService.Instance.initiatePromiseWithXhr(dataPromiseWithXhr); // handles both .errors and logout errors

    return dataPromiseWithXhr;
  }

  public getBankAccountsBySearchQueryWithXHR(searchQuery?: string) {
    const filters = [
      {
        type: 'Type',
        comparison: 'Equals',
        value: 'BankAccount',
      },
    ];

    if ($.trim(searchQuery)) {
      filters.push({
        type: 'Name',
        comparison: 'Contains',
        value: searchQuery,
      });
    }

    return this.getFundAccountsByFiltersWithXHR(filters);
  }

  public getPrefundedWalletsAndBankAccountsBySearchQueryWithXHR(searchQuery: string) {
    const filters = [
      {
        type: 'Type',
        comparison: 'Equals',
        value: 'PrefundedWalletAndBankAccount',
      },
    ];

    if ($.trim(searchQuery)) {
      filters.push({
        type: 'Name',
        comparison: 'Contains',
        value: searchQuery,
      });
    }

    return this.getFundAccountsByFiltersWithXHR(filters);
  }

  public getAccountsBySearchQueryWithXHR(searchQuery) {
    const filters = [];

    if ($.trim(searchQuery)) {
      filters.push({
        type: 'Name',
        comparison: 'Contains',
        value: searchQuery,
      });
    }
    return this.getFundAccountsByFiltersWithXHR(filters);
  }

  public getFundAccountsByFiltersWithXHR(filters: ReadonlyArray<any>) {
    const companyId: string = this.companyService.CompanyId();
    if (companyId === undefined) {
      return undefined;
    }

    const dataPromiseWithXhr = new xhrService.PromiseWithXhr<
      Array<viewModels.FundAccountViewModel>
    >();

    dataPromiseWithXhr.getXhr = () =>
      this.userService.AuthorizedGetFromUri(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/accounts`,
        serializeQueryWithoutKeyEncoding({
          filters,
          resultsPerPage: 50,
        })
      ) as JQueryXHR;

    xhrService.Instance.initiatePromiseWithXhr(dataPromiseWithXhr); // handles both .errors and logout errors

    return dataPromiseWithXhr;
  }
}
export const Instance = new BankService();
